/**
 * @bos/portal3 — THE ER PICTURE'S LAYOUT (phase 2): a pure, React-free module that turns
 * the data model the room already holds into placed boxes, routed edges and labels.
 *
 * WHY A PURPOSE-BUILT LAYOUT AND NOT A LIBRARY. The chain writes `er-diagram.mmd` every
 * run, and the honest reason the portal never drew it was the weight of a Mermaid bundle.
 * A 3–20 entity FK graph does not need a force simulation: it needs a LAYERED layout, and
 * that is ~200 lines of arithmetic over the payload the review already fetched. No new
 * dependency, no second fetch, no second source of truth — the Picture and the Cards are
 * two renderings of the same bytes, which is why they can never disagree.
 *
 * THE ALGORITHM (proven against both real runs before it was written here):
 *   1. LAYERING — longest-path over the FK digraph (edges child → referenced entity).
 *      Sinks (referenced-only entities: RoomType, User, Guest…) land in layer 0; the
 *      column index is `maxLayer − layer`, so the deepest child sits leftmost and EVERY
 *      edge flows left → right, the way the relationship sentence reads ("Room —many-to-one
 *      · via roomTypeId→ RoomType"). An isolated entity (HotelSettings) lands with the
 *      sinks. The sweep is bounded by node count, so a cyclic input — none exists in a real
 *      model; the chain's own laws forbid FK cycles — degrades the picture and never hangs.
 *   2. ORDERING — barycenter sweeps (4 passes), sorting each column by the mean position
 *      of its neighbours. This is what puts Folio beside Reservation and RoomType beside
 *      Room without one hand-placed coordinate.
 *   3. COORDINATES — MEASURED boxes: height = title bar + a row per field, width = the
 *      widest field row (glyph-width estimate, clamped 200–360px). SVG text does not
 *      reflow, so a fixed width is exactly what makes rows collide at real density
 *      (Reservation.sourceKind's name+type+enum overflowed a fixed box in the first draft).
 *   4. EDGES — an edge between ADJACENT columns is one cubic curve from the FK field's own
 *      row to the target's title bar: the connection is anchored to the fact that makes it
 *      true. An edge spanning more than one column (or landing inside its own column)
 *      routes down into a BUS LANE below the diagram body, across, and back up — so no
 *      long edge is ever drawn across a box it does not concern. Parallel edges (Receipt →
 *      User via issuedById AND via voidedById) each get their own lane and their own `via`
 *      label; nothing is merged, because the FK is what distinguishes them.
 *   5. LABELS — a gap label is at most TWO short stacked lines (cardinality, then "via fk ·
 *      optional"), de-conflicted per gap by position (a block that would overlap the one
 *      above it moves below it); one long line does not fit a 170px gap. A bus label sits on
 *      its own lane and may be a full sentence. A SELF-relationship (from === to) draws no
 *      line at all — it is annotated under its own box, because any routed line would cross
 *      the box it belongs to.
 *
 * Everything here is arithmetic over the payload: no fetch, no measurement of the DOM, no
 * randomness. The same model always lays out the same way, which is what makes the picture
 * testable at all.
 */

// ── what the layout reads (a structural subset of the served DataModelDoc) ────

export interface ErLayoutField {
  readonly name: string;
  readonly type: string;
  readonly format?: string;
  readonly enumRef?: string;
}

export interface ErLayoutEntity {
  readonly name: string;
  readonly identityField: string | null;
  readonly fields: readonly ErLayoutField[];
}

export interface ErLayoutRelationship {
  readonly from: string;
  readonly to: string;
  readonly cardinality: string | null;
  readonly foreignKeyField: string | null;
  readonly optional: boolean | null;
}

export interface ErLayoutInput {
  readonly entities: readonly ErLayoutEntity[];
  readonly relationships: readonly ErLayoutRelationship[];
}

// ── what the layout produces ─────────────────────────────────────────────────

export interface ErBox {
  readonly entity: string;
  /** Position in the input's `entities` — the renderer's key back to the entity's own data. */
  readonly index: number;
  readonly column: number;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

export interface ErEdge {
  /** Position in the input's `relationships` — the identity a click reports back. */
  readonly index: number;
  readonly from: string;
  readonly to: string;
  readonly foreignKeyField: string | null;
  readonly optional: boolean;
  /** The row of the source box the edge leaves from (the FK field's own row; 0 when the
   *  model names an FK the entity does not carry — the picture still points somewhere true
   *  about the box, and the label still says which field the model claims). */
  readonly rowIndex: number;
  /** How many columns the edge crosses (0 = both ends in one column). */
  readonly span: number;
  /** −1 = drawn as a curve in the column gap; ≥0 = routed through that bus lane. */
  readonly lane: number;
  readonly d: string;
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
}

export interface ErLabel {
  readonly x: number;
  readonly y: number;
  readonly text: string;
  /** A bus label is a full sentence on its own lane; a gap label is one of two short lines. */
  readonly bus: boolean;
}

export interface ErLayout {
  readonly boxes: readonly ErBox[];
  readonly edges: readonly ErEdge[];
  readonly labels: readonly ErLabel[];
  readonly width: number;
  readonly height: number;
  /** The bottom of the last box — where the bus lanes begin. */
  readonly bodyBottom: number;
  readonly busLanes: number;
  readonly columns: number;
}

// ── the constants the renderer draws with (one home, so CSS and geometry agree) ──

/** Height of an entity's title bar. */
export const TITLE_H = 30;
/** Height of one field row. */
export const ROW_H = 19;
/** Padding under the last field row, inside the box. */
export const BOX_PAD = 8;
/** The gap between columns — the room the two-line edge labels live in. */
export const COL_GAP = 170;
/** The gap between stacked boxes in a column. */
export const ROW_GAP = 36;
/** The canvas margin on every side. */
export const MARGIN = 28;
/** The height of one bus lane below the diagram body. */
export const BUS_LANE_H = 22;

/** Box height: the title bar, a row per field, and the bottom padding. */
export function boxHeight(entity: ErLayoutEntity): number {
  return TITLE_H + entity.fields.length * ROW_H + BOX_PAD;
}

/**
 * MEASURED box width: the widest field row, estimated at average glyph widths and clamped
 * to 200–360px. SVG text does not reflow, so a width that ignores content is exactly what
 * makes a name + type + enum badge collide inside its own box.
 */
export function boxWidth(entity: ErLayoutEntity): number {
  let w = 8 * (entity.name.length + 2) + 20;
  for (const f of entity.fields) {
    const marks =
      (f.enumRef !== undefined ? 6.2 * (f.enumRef.length + 3) : 0) +
      (f.format !== undefined ? 6 * (f.format.length + 1) : 0) +
      (f.name === entity.identityField ? 12 : 0);
    w = Math.max(w, 6.6 * f.name.length + 6.6 * f.type.length + marks + 44);
  }
  return Math.min(360, Math.max(200, Math.ceil(w)));
}

/** The vertical centre of one field row, in canvas coordinates. */
export function fieldRowCenterY(box: ErBox, rowIndex: number): number {
  return box.y + TITLE_H + rowIndex * ROW_H + ROW_H / 2;
}

/** The baseline a field row's text sits on, in canvas coordinates. */
export function fieldRowBaselineY(box: ErBox, rowIndex: number): number {
  return box.y + TITLE_H + rowIndex * ROW_H + ROW_H - 6;
}

// ── the relationship, in the words the picture uses ──────────────────────────

/** The whole relationship on one line — what a bus lane's own label says. */
export function edgeLabel(r: ErLayoutRelationship): string {
  const parts: string[] = [];
  if (r.cardinality !== null) parts.push(r.cardinality);
  if (r.foreignKeyField !== null) parts.push(`via ${r.foreignKeyField}`);
  if (r.optional === true) parts.push('optional');
  return parts.join(' · ');
}

/**
 * A gap label as SHORT STACKED LINES. A 170px gap cannot hold "many-to-one · via
 * assignedRoomId · optional" on one line — it walks over the box next to it — but it holds
 * "many-to-one" over "via assignedRoomId · optional" comfortably.
 */
export function edgeLabelLines(r: ErLayoutRelationship): string[] {
  const lines: string[] = [];
  if (r.cardinality !== null) lines.push(r.cardinality);
  const second = [
    r.foreignKeyField !== null ? `via ${r.foreignKeyField}` : null,
    r.optional === true ? 'optional' : null,
  ]
    .filter((s): s is string => s !== null)
    .join(' · ');
  if (second !== '') lines.push(second);
  return lines;
}

// ── the layout itself ────────────────────────────────────────────────────────

export function layoutDataModel(model: ErLayoutInput): ErLayout {
  const entities = model.entities;
  const names = entities.map((e) => e.name);
  const index = new Map<string, number>();
  names.forEach((n, i) => {
    if (!index.has(n)) index.set(n, i); // a duplicated name resolves to its first box
  });

  // Outgoing FK targets (child → referenced parent).
  const out: number[][] = names.map(() => []);
  for (const r of model.relationships) {
    const a = index.get(r.from);
    const b = index.get(r.to);
    if (a !== undefined && b !== undefined && a !== b) out[a].push(b);
  }

  // 1. Longest-path layering. Bounded by node count: a cycle simply stops raising layers.
  const layer = names.map(() => 0);
  let moved = true;
  for (let pass = 0; pass < names.length && moved; pass++) {
    moved = false;
    for (let v = 0; v < names.length; v++) {
      for (const t of out[v]) {
        if (layer[v] < layer[t] + 1) {
          layer[v] = layer[t] + 1;
          moved = true;
        }
      }
    }
  }
  // 1b. COMPACT the layers to a contiguous 0..k range. On a DAG, longest-path layers are
  //     already contiguous (a node at layer k+1 was forced there by a neighbour at layer k),
  //     so this is the identity on every real model. An FK CYCLE, though, inflates layer
  //     numbers as the bounded sweep cuts off — and every unoccupied layer in between would
  //     become a 170px BLANK column, with same-column edges bus-routed straight through the
  //     boxes. Compacting makes cyclic input degrade to a DENSE picture, never an inflated one.
  const usedLayers = [...new Set(layer)].sort((p, q) => p - q);
  const denseOf = new Map(usedLayers.map((value, idx) => [value, idx]));
  for (let v = 0; v < layer.length; v++) layer[v] = denseOf.get(layer[v])!;
  const maxLayer = names.length === 0 ? 0 : Math.max(...layer);
  const colOf = layer.map((l) => maxLayer - l); // deepest child leftmost, references right

  // 2. Barycenter ordering within each column (4 alternating sweeps).
  const cols: number[][] = [];
  for (let c = 0; c <= maxLayer; c++) {
    cols.push(names.map((_, i) => i).filter((i) => colOf[i] === c));
  }
  const neighbours: number[][] = names.map((_, v) => {
    const n = [...out[v]];
    for (let u = 0; u < names.length; u++) if (out[u].includes(v)) n.push(u);
    return n;
  });
  const pos = names.map(() => 0);
  const reindex = (): void => {
    for (const col of cols) col.forEach((v, i) => (pos[v] = i));
  };
  reindex();
  const bary = (v: number): number => {
    const n = neighbours[v];
    return n.length === 0 ? pos[v] : n.reduce((s, u) => s + pos[u], 0) / n.length;
  };
  for (let sweep = 0; sweep < 4; sweep++) {
    for (const col of cols) {
      col.sort((a, b) => bary(a) - bary(b));
      reindex();
    }
  }

  // 3. Coordinates. Columns left → right; boxes stacked with measured heights; each column
  //    is as wide as its widest box.
  const x = names.map(() => 0);
  const y = names.map(() => 0);
  const w = names.map((_, v) => boxWidth(entities[v]));
  let colX = MARGIN;
  for (const col of cols) {
    const colW = col.length === 0 ? 0 : Math.max(...col.map((v) => w[v]));
    let colY = MARGIN;
    for (const v of col) {
      x[v] = colX;
      y[v] = colY;
      colY += boxHeight(entities[v]) + ROW_GAP;
    }
    colX += colW + COL_GAP;
  }
  const boxes: ErBox[] = entities.map((e, v) => ({
    entity: e.name,
    index: v,
    column: colOf[v],
    x: x[v],
    y: y[v],
    w: w[v],
    h: boxHeight(e),
  }));
  const bodyBottom = boxes.reduce((m, b) => Math.max(m, b.y + b.h), MARGIN);

  // 4. Edges. Span 1 ⇒ a curve through the column gap; anything else ⇒ its own bus lane.
  const labels: ErLabel[] = [];
  const edges: ErEdge[] = [];
  /** Gap-label BLOCKS, collected per column gap and de-conflicted after the loop — by the
   *  blocks' own positions, not by an arrival-order parity counter, so ANY two that would
   *  overlap separate (the counter left non-adjacent crossing pairs stacked). */
  const gapBlocks: Array<{ gap: number; x: number; y: number; lines: string[] }> = [];
  /** How many self-relationship notes each box already wears, so several stack. */
  const selfNotes = new Map<number, number>();
  let busLanes = 0;
  for (let i = 0; i < model.relationships.length; i++) {
    const r = model.relationships[i];
    const a = index.get(r.from);
    const b = index.get(r.to);
    if (a === undefined || b === undefined) continue; // a relationship naming no box is not drawn

    if (a === b) {
      // A SELF-relationship has no second box to route to: drawn as an edge, its span-0 bus
      // legs would run inside the box's own span, threading the line through the box and
      // everything below it. So it is not drawn as a line at all — the fact is ANNOTATED on
      // the box instead, and the "How they connect" list beside the picture keeps the full
      // sentence. Honest, and nothing crosses a box it does not concern.
      const worn = selfNotes.get(a) ?? 0;
      selfNotes.set(a, worn + 1);
      labels.push({
        x: x[a] + w[a] / 2,
        y: y[a] + boxHeight(entities[a]) + 24 + worn * 12,
        text: `↩ names another ${r.to}${r.foreignKeyField !== null ? ` · via ${r.foreignKeyField}` : ''}`,
        bus: false,
      });
      continue;
    }

    const span = Math.abs(colOf[a] - colOf[b]);
    const lane = span === 1 ? -1 : busLanes++;

    const fkRow = entities[a].fields.findIndex((f) => f.name === r.foreignKeyField);
    const rowIndex = fkRow >= 0 ? fkRow : 0;
    const leftToRight = x[a] < x[b];
    const x1 = x[a] + (leftToRight ? w[a] : 0);
    // A ZERO-FIELD box has no row 0 to anchor to — its nominal first row sits below the box
    // bottom. The box's vertical centre is the truth the picture can point at instead.
    const y1 = entities[a].fields.length === 0 ? y[a] + boxHeight(entities[a]) / 2 : fieldRowCenterY(boxes[a], rowIndex);
    const x2 = x[b] + (leftToRight ? 0 : w[b]);
    const y2 = y[b] + TITLE_H / 2;

    let d: string;
    if (lane < 0) {
      const mx = (x1 + x2) / 2;
      d = `M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`;
      const lines = edgeLabelLines(r);
      if (lines.length > 0) {
        gapBlocks.push({ gap: Math.min(colOf[a], colOf[b]), x: mx, y: (y1 + y2) / 2 - 8, lines });
      }
    } else {
      const busY = bodyBottom + 26 + lane * BUS_LANE_H;
      d = [
        `M ${x1} ${y1}`,
        `C ${x1 + 40} ${y1}, ${x1 + 46} ${busY}, ${x1 + 86} ${busY}`,
        `L ${x2 - 86} ${busY}`,
        `C ${x2 - 46} ${busY}, ${x2 - 40} ${y2}, ${x2} ${y2}`,
      ].join(' ');
      labels.push({
        x: (x1 + x2) / 2,
        y: busY - 6,
        text: `${r.from} → ${r.to} · ${edgeLabel(r)}`,
        bus: true,
      });
    }

    edges.push({
      index: i,
      from: r.from,
      to: r.to,
      foreignKeyField: r.foreignKeyField,
      optional: r.optional === true,
      rowIndex,
      span,
      lane,
      d,
      x1,
      y1,
      x2,
      y2,
    });
  }

  // De-conflict the gap labels, per gap: sort by natural position, then push any block that
  // would overlap the one above it DOWN past it. Deterministic, identity-driven, and a no-op
  // wherever the midpoints already differ enough — which is every real model today.
  const byGap = new Map<number, Array<(typeof gapBlocks)[number]>>();
  for (const block of gapBlocks) {
    const list = byGap.get(block.gap);
    if (list === undefined) byGap.set(block.gap, [block]);
    else list.push(block);
  }
  for (const list of byGap.values()) {
    list.sort((p, q) => p.y - q.y);
    let floor = -Infinity;
    for (const block of list) {
      if (block.y < floor) block.y = floor;
      floor = block.y + block.lines.length * 12 + 4;
    }
  }
  for (const block of gapBlocks) {
    block.lines.forEach((text, li) => labels.push({ x: block.x, y: block.y + li * 12, text, bus: false }));
  }

  return {
    boxes,
    edges,
    labels,
    width: colX - COL_GAP + MARGIN,
    height: bodyBottom + (busLanes > 0 ? busLanes * BUS_LANE_H + 34 : 0) + MARGIN,
    bodyBottom,
    busLanes,
    columns: cols.length,
  };
}
